import { useEffect, useState } from "react";
import { ArrowsClockwise, FloppyDisk, PaperPlaneTilt } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { NumberField } from "../DailyReportForm";
import { btnGhost, btnPrimary, fmtDateTimeWIB, fmtIDR, inp, todayWIB } from "../util";
import { CHANNELS, Card, JStatus, OwnerActions, Pill, Revisions, Row, call, useLoad } from "./common";
import JubelioDetail from "./JubelioDetail";
import ReconCard from "./Recon";

const blank = (cfg) => ({ internal_sales_idr: 0, internal_orders: 0, internal_ongkir_idr: 0, shopee_sales_idr: 0, shopee_orders: 0, tokopedia_sales_idr: 0, tokopedia_orders: 0,
    discount_idr: 0, return_count: 0, return_idr: 0, reconciliation_notes: "",
    po: cfg.po_countries.map((c) => ({ country: c, sales_idr: 0, orders: 0, ongkir_idr: 0 })) });
const SHORT = { internal: "WhatsApp / IG", shopee: "Shopee", tokopedia: "Tokopedia" };

function withCountries(d, cfg) {
    const have = new Set(d.po.map((p) => p.country));
    return { ...d, po: [...d.po.map((p) => ({ ongkir_idr: 0, ...p })), ...cfg.po_countries.filter((c) => !have.has(c)).map((c) => ({ country: c, sales_idr: 0, orders: 0, ongkir_idr: 0 }))] };
}

export function closingTotals(d) {
    const rs = d.internal_sales_idr + d.shopee_sales_idr + d.tokopedia_sales_idr, ro = d.internal_orders + d.shopee_orders + d.tokopedia_orders;
    const ps = d.po.reduce((a, p) => a + p.sales_idr, 0), po = d.po.reduce((a, p) => a + p.orders, 0);
    const ongkir = (d.internal_ongkir_idr || 0) + d.po.reduce((a, p) => a + (p.ongkir_idr || 0), 0);
    return { rs, ro, ps, po, grand: rs + ps, orders: ro + po, ongkir };
}

const signed = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${fmtIDR(Math.abs(n))}`;

function Cell({ label, value, sub, cls = "", testid }) {
    return <div className="min-w-0" data-testid={testid}><div className="text-[10px] uppercase tracking-wider text-neutral-400">{label}</div><div className={`tabular-nums font-medium break-words ${cls}`}>{value}</div><div className="text-[11px] text-neutral-500">{sub}</div></div>;
}

function Compare({ k, l, c, partial }) {
    const x = c.jubelio_checks?.[k] || {}, a = c.data || {};
    const has = x.jubelio_sales_idr !== undefined;
    const dcls = !has ? "" : !(x.diff_sales_idr || x.diff_orders) ? "text-emerald-700" : partial ? "text-orange-700" : "text-rose-700";
    return (
        <div className="py-2 border-b border-mist/60 last:border-0" data-testid={`jcmp-${k}`}>
            <div className="flex justify-between items-center gap-2 text-sm"><span className="font-medium text-ink">{l}</span><span className="flex items-center gap-2">{has && <JubelioDetail cid={c.id} k={k} label={l} />}<JStatus s={x.status} testid={`jcheck-${k}`} /></span></div>
            <div className="grid grid-cols-3 gap-2 mt-1 text-xs">
                <Cell label="Admin" value={fmtIDR(a[`${k}_sales_idr`])} sub={`${a[`${k}_orders`] || 0} order`} testid={`jcmp-${k}-admin`} />
                <Cell label={partial ? "Jubelio s/d kini" : "Jubelio"} value={has ? fmtIDR(x.jubelio_sales_idr) : "—"} sub={has ? `${x.jubelio_orders} order` : "belum ada data"} testid={`jcmp-${k}-jubelio`} />
                <Cell label={partial ? "Selisih saat ini" : "Selisih"} value={has ? signed(x.diff_sales_idr) : "—"} sub={has ? `${x.diff_orders > 0 ? "+" : ""}${x.diff_orders} order` : ""} cls={dcls} testid={`jcmp-${k}-diff`} />
            </div>
        </div>
    );
}

function JubelioBox({ c, canVerify, setC }) {
    const j = c.jubelio_checks || {};
    const partial = c.jubelio?.state === "PARTIAL" || Object.values(j).some((x) => x.status === "PERLU_DITINJAU");
    return (
        <Card title="Verifikasi Jubelio" testid="closing-jubelio" right={canVerify && c.id && c.status !== "APPROVED" && (
            <button type="button" className={btnGhost} data-testid="jubelio-recheck-btn" onClick={async () => { const r = await call(() => api.post(`/reporting/v2/sales-closing/${c.id}/recheck`), "Jubelio diperiksa ulang"); if (r) setC(r); }}><ArrowsClockwise size={14} /> Periksa Ulang Jubelio</button>)}>
            {c.period?.start_at && <p className="text-[11px] text-neutral-500 mb-1" data-testid="jubelio-period">Periode dibandingkan: {c.period.label}</p>}
            {CHANNELS.map(([k, l]) => (canVerify ? <Compare key={k} k={k} l={l} c={c} partial={partial} /> : (
                <div key={k} className="flex justify-between py-1 text-sm">
                    <span className="text-neutral-500">{l}</span><JStatus s={j[k]?.status} testid={`jcheck-${k}`} />
                </div>
            )))}
            {canVerify && <p className="text-[11px] text-neutral-400 mt-1">Selisih = Admin − Jubelio.</p>}
            {partial && <p className="text-[11px] text-orange-700 mt-2" data-testid="jubelio-partial-note">Hari transaksi belum selesai. Data Jubelio dapat berubah sampai hari berakhir.</p>}
            {canVerify && c.jubelio?.reason && c.jubelio.state !== "PARTIAL" && <p className="text-[11px] text-neutral-500 mt-2" data-testid="jubelio-reason">Keterangan: {c.jubelio.reason}</p>}
            <p className="text-[11px] text-neutral-400 mt-2">PO Baru tidak dicocokkan ke Jubelio. Penjualan marketplace bukan bukti uang sudah masuk rekening.</p>
        </Card>
    );
}

function ApprovalSnapshot({ a, i }) {
    const s = a.jubelio_snapshot || {}, j = s.jubelio_checks || {};
    return (
        <div className="py-2 border-b border-mist/60 last:border-0" data-testid={`approval-history-item-${i}`}>
            <div>Disetujui {a.approved_at ? fmtDateTimeWIB(a.approved_at) : "—"} · {a.approved_by || "—"}{a.override_reason ? ` · alasan: “${a.override_reason}”` : ""}</div>
            <div className="text-neutral-500">Dibuka ulang {fmtDateTimeWIB(a.reopened_at)} · {a.reopened_by} · “{a.reopen_reason}”</div>
            <div className="text-neutral-500">Snapshot Jubelio{s.jubelio?.state ? ` (${s.jubelio.state})` : ""}:</div>
            {s.captured_on === "reopen" && <div className="text-orange-700" data-testid={`approval-history-legacy-${i}`}>Snapshot lama — hasil Jubelio yang tersimpan saat dibuka ulang; belum tentu sama dengan hasil saat disetujui</div>}
            {CHANNELS.map(([k, l]) => (
                <div key={k} className="flex justify-between gap-2 tabular-nums" data-testid={`approval-history-${i}-${k}`}>
                    <span>{l}</span>
                    <span>{j[k]?.jubelio_sales_idr !== undefined ? `${fmtIDR(j[k].jubelio_sales_idr)} · ${j[k].jubelio_orders} order · selisih ${signed(j[k].diff_sales_idr)}` : "—"} · {j[k]?.status || "—"}</span>
                </div>
            ))}
        </div>
    );
}

function ApprovalHistory({ list }) {
    if (!list?.length) return null;
    return (
        <details className="text-xs text-neutral-600" data-testid="approval-history">
            <summary className="cursor-pointer uppercase tracking-widest text-neutral-500">Riwayat persetujuan ({list.length})</summary>
            {list.map((a, i) => <ApprovalSnapshot key={i} a={a} i={i} />)}
        </details>
    );
}

function ReconNotes({ d, set, c }) {
    const flagged = Object.values(c.jubelio_checks || {}).some((x) => x.status && x.status !== "SESUAI");
    return (
        <Card title="Catatan Selisih / Reconciliation Notes" testid="closing-recon-notes">
            {flagged && <p className="text-xs text-amber-800 mb-2" data-testid="recon-notes-hint">Jelaskan penyebab selisih berdasarkan hasil Verifikasi Jubelio di atas. Tidak perlu menghitung ulang selisih secara manual.</p>}
            <textarea value={d.reconciliation_notes || ""} onChange={(e) => set("reconciliation_notes", e.target.value)} maxLength={2000} rows={3}
                placeholder="Opsional bila semua Sesuai. Contoh: 1 order Internal tercatat besok di Jubelio." className={`${inp} ${flagged ? "!border-amber-400" : ""}`} data-testid="recon-notes-input" />
        </Card>
    );
}

export function NotRequired({ rd, testid }) {
    return <Card title={`Tanggal ${rd}`} testid={testid}><p className="text-sm text-neutral-600">Hari ini Tidak Wajib Lapor — transaksi otomatis masuk laporan hari kerja berikutnya.</p></Card>;
}

function Period({ p }) {
    return (
        <div className={`text-xs rounded-sm border px-3 py-2 ${p.carry_forward ? "border-amber-300 bg-amber-50 text-amber-900" : "border-mist text-neutral-600"}`} data-testid="closing-period">
            <b>Periode transaksi:</b> {p.label}
            {p.carry_forward && <div className="mt-0.5">Termasuk transaksi hari Tidak Wajib Lapor: {p.carried_days.join(", ")}. Cukup kirim satu laporan.</div>}
        </div>
    );
}

export default function SalesClosing({ cfg, date }) {
    const { user } = useAuth();
    const isOwner = user?.role === "owner";
    const rd = date || todayWIB();
    const [c, , setC] = useLoad("/reporting/v2/sales-closing", { date: rd });
    const [d, setD] = useState(null);
    useEffect(() => { if (c) setD(withCountries({ ...blank(cfg), ...(c.data || {}) }, cfg)); }, [c, cfg]);
    if (!c || !d) return <div className="text-sm text-neutral-400">Memuat…</div>;
    if (c.not_required) return <NotRequired rd={rd} testid="closing-not-required" />;
    const locked = c.status === "APPROVED";
    const t = closingTotals(d);
    const set = (k, v) => setD({ ...d, [k]: v });
    const setPo = (i, k, v) => setD({ ...d, po: d.po.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
    const save = async (submit) => {
        const r = await call(() => api.put("/reporting/v2/sales-closing", { data: { ...d, po: d.po.filter((p) => p.sales_idr || p.orders || cfg.po_countries.includes(p.country)) }, submit }, { params: { date: rd } }),
            submit || c.first_submitted_at ? "Sales Closing terkirim" : "Draf disimpan");
        if (r) setC(r);
    };
    return (
        <div className="space-y-4 max-w-3xl" data-testid="sales-closing">
            <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500">
                <span className="text-sm text-ink font-medium">Tanggal {rd}</span><Pill status={c.status} testid="closing-status" />
                {c.first_submitted_at && <span>Dikirim pertama {fmtDateTimeWIB(c.first_submitted_at)} oleh {c.first_submitted_by}</span>}
                {c.updated_by && <span>· PIC terakhir: {c.updated_by}</span>}
            </div>
            {c.period && <Period p={c.period} />}
            <fieldset disabled={locked} className="space-y-4">
                <Card title="A. Ready Stock">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {CHANNELS.map(([k]) => [
                            <NumberField key={`${k}s`} k={`${k}_sales_idr`} label={`Sales ${SHORT[k]}`} type="money" value={d[`${k}_sales_idr`]} onChange={set} />,
                            <NumberField key={`${k}o`} k={`${k}_orders`} label={`Order ${SHORT[k]}`} type="count" value={d[`${k}_orders`]} onChange={set} />,
                            k === "internal" && <NumberField key="io" k="internal_ongkir_idr" label="Ongkir Internal" type="money" value={d.internal_ongkir_idr} onChange={set} hint="Dicatat terpisah, tidak masuk Sales" />,
                            k === "internal" && <div key="isp" className="hidden sm:block" />,
                        ])}
                    </div>
                    <Row label="Total Penjualan Ready Stock" value={`${fmtIDR(t.rs)} · ${t.ro} order`} bold testid="closing-ready-total" />
                </Card>
                <Card title="B. PO Baru">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {d.po.map((p, i) => [
                            <NumberField key={`${p.country}s`} k={`po-${i}-sales`} label={`Sales PO ${p.country}`} type="money" value={p.sales_idr} onChange={(_, v) => setPo(i, "sales_idr", v)} />,
                            <NumberField key={`${p.country}o`} k={`po-${i}-orders`} label="Order" type="count" value={p.orders} onChange={(_, v) => setPo(i, "orders", v)} />,
                            <NumberField key={`${p.country}g`} k={`po-${i}-ongkir`} label={`Ongkir PO ${p.country}`} type="money" value={p.ongkir_idr || 0} onChange={(_, v) => setPo(i, "ongkir_idr", v)} />,
                        ])}
                    </div>
                    <Row label="Total Penjualan PO Baru" value={`${fmtIDR(t.ps)} · ${t.po} order`} bold testid="closing-po-total" />
                </Card>
                <Card title="Informasi: Discount & Return / Refund" testid="closing-info">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <NumberField k="discount_idr" label="Total Discount" type="money" value={d.discount_idr} onChange={set} />
                        <NumberField k="return_count" label="Jumlah Return / Refund" type="count" value={d.return_count} onChange={set} />
                        <NumberField k="return_idr" label="Nominal Return / Refund" type="money" value={d.return_idr} onChange={set} />
                    </div>
                </Card>
                <ReconNotes d={d} set={set} c={c} />
            </fieldset>
            <Card title="C. Total Penjualan" testid="closing-totals">
                <Row label="Total Ready Stock" value={fmtIDR(t.rs)} /><Row label="Total PO Baru" value={fmtIDR(t.ps)} />
                <Row label="Grand Total Penjualan" value={fmtIDR(t.grand)} bold testid="closing-grand-total" /><Row label="Total Order" value={t.orders} testid="closing-total-orders" />
                <Row label="Ongkir (terpisah, tidak termasuk Grand Total)" value={fmtIDR(t.ongkir)} testid="closing-ongkir-total" />
            </Card>
            {!locked && (
                <div className="flex flex-wrap gap-2">
                    {!c.first_submitted_at && <button type="button" className={btnGhost} onClick={() => save(false)} data-testid="closing-draft-btn"><FloppyDisk size={14} /> Simpan draf</button>}
                    <button type="button" className={btnPrimary} onClick={() => save(true)} data-testid="closing-submit-btn"><PaperPlaneTilt size={14} /> {c.first_submitted_at ? "Simpan koreksi" : "Kirim Sales Closing"}</button>
                </div>
            )}
            {c.first_submitted_at && <JubelioBox c={c} canVerify={isOwner || ["supervisor", "admin_sales"].includes(user?.role)} setC={setC} />}
            {user?.role === "admin_sales" && <ReconCard date={rd} role={user.role} />}
            {isOwner && (
                <div className="space-y-3">
                    {c.first_submitted_at && (
                        <div className="text-xs rounded-sm border border-mist bg-white px-3 py-2" data-testid="owner-recon-notes">
                            <b>Catatan Selisih dari Admin:</b> {c.data?.reconciliation_notes?.trim() || <span className="text-neutral-400">tidak ada catatan</span>}
                        </div>
                    )}
                    <OwnerActions doc={c} base="/reporting/v2/sales-closing" onDone={setC} approveBody={() => {
                        const ok = Object.values(c.jubelio_checks || {}).length === 3 && Object.values(c.jubelio_checks).every((x) => x.status === "SESUAI");
                        if (ok) return {};
                        const reason = window.prompt("Cek Jubelio belum Sesuai. Tulis alasan untuk tetap menyetujui:");
                        return reason && reason.trim() ? { reason } : null;
                    }} />
                    <Revisions list={c.revisions} />
                    <ApprovalHistory list={c.approval_history} />
                </div>
            )}
        </div>
    );
}
